import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, LayoutDashboard, BarChart3, Building2, Package, Users, MessageSquare } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useUI } from '../context/UIContext';
import axios from 'axios';
import { motion } from 'motion/react';


// ── Types ─────────────────────────────────────────────────────────────────────
interface TickerItem {
  symbol: string;
  price: string;
  change: string;
  icon: () => React.ReactNode;
}

// ── Binance crypto symbols to track ───────────────────────────────────────────
const CRYPTO_SYMBOLS = ['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT', 'XRPUSDT', 'DOGEUSDT', 'ADAUSDT', 'LTCUSDT', 'LINKUSDT', 'MATICUSDT', 'DOTUSDT', 'AVAXUSDT', 'UNIUSDT'];

const CRYPTO_DISPLAY: Record<string, string> = {
  BTCUSDT: 'BTC/USD',
  ETHUSDT: 'ETH/USD',
  BNBUSDT: 'BNB/USD',
  SOLUSDT: 'SOL/USD',
  XRPUSDT: 'XRP/USD',
  DOGEUSDT: 'DOGE/USD',
  ADAUSDT: 'ADA/USD',
  LTCUSDT: 'LTC/USD',
  LINKUSDT: 'LINK/USD',
  MATICUSDT: 'MATIC/USD',
  DOTUSDT: 'DOT/USD',
  AVAXUSDT: 'AVAX/USD',
  UNIUSDT: 'UNI/USD',
};

// Crypto logos from open-source cryptocurrency-icons (jsDelivr CDN)
const CI = (coin: string) => `https://cdn.jsdelivr.net/gh/spothq/cryptocurrency-icons@master/32/color/${coin}.png`;
const CRYPTO_LOGO: Record<string, string> = {
  BTCUSDT: CI('btc'),
  ETHUSDT: CI('eth'),
  BNBUSDT: CI('bnb'),
  SOLUSDT: CI('sol'),
  XRPUSDT: CI('xrp'),
  DOGEUSDT: CI('doge'),
  ADAUSDT: CI('ada'),
  LTCUSDT: CI('ltc'),
  LINKUSDT: CI('link'),
  MATICUSDT: CI('matic'),
  DOTUSDT: CI('dot'),
  AVAXUSDT: CI('avax'),
  UNIUSDT: CI('uni'),
};

// ── Brand logo helper ──────────────────────────────────────────────────────────
const BrandLogo = ({ src, alt }: { src: string; alt: string }) => (
  <img
    src={src}
    alt={alt}
    className="w-4 h-4 rounded-sm object-contain"
    onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
  />
);

// ── Indian stock base values (live from API) ───────────
const G_FAVICON = (domain: string) => `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;

const INDIAN_STOCKS_BASE = [
  { symbol: 'NIFTY', base: 0, name: 'NIFTY 50', nseSymbol: 'NIFTY', icon: () => <BrandLogo src={G_FAVICON('nseindia.com')} alt="NSE" /> },
  { symbol: 'BANKNIFTY', base: 0, name: 'NIFTY BANK', nseSymbol: 'BANKNIFTY', icon: () => <BrandLogo src={G_FAVICON('nseindia.com')} alt="NSE" /> },
  { symbol: 'SENSEX', base: 0, name: 'SENSEX', nseSymbol: 'SENSEX', icon: () => <BrandLogo src={G_FAVICON('bseindia.com')} alt="BSE" /> },
  { symbol: 'RELIANCE', base: 0, name: 'Reliance Industries', nseSymbol: 'RELIANCE', icon: () => <BrandLogo src={G_FAVICON('ril.com')} alt="Reliance" /> },
  { symbol: 'TCS', base: 0, name: 'Tata Consultancy Services', nseSymbol: 'TCS', icon: () => <BrandLogo src={G_FAVICON('tcs.com')} alt="TCS" /> },
  { symbol: 'HDFCBANK', base: 0, name: 'HDFC Bank', nseSymbol: 'HDFCBANK', icon: () => <BrandLogo src={G_FAVICON('hdfcbank.com')} alt="HDFC" /> },
  { symbol: 'INFY', base: 0, name: 'Infosys', nseSymbol: 'INFY', icon: () => <BrandLogo src={G_FAVICON('infosys.com')} alt="Infosys" /> },
  { symbol: 'ITC', base: 0, name: 'ITC Limited', nseSymbol: 'ITC', icon: () => <BrandLogo src={G_FAVICON('itcportal.com')} alt="ITC" /> },
  { symbol: 'LT', base: 0, name: 'Larsen & Toubro', nseSymbol: 'LT', icon: () => <BrandLogo src={G_FAVICON('larsentoubro.com')} alt="L&T" /> },
  { symbol: 'MARUTI', base: 0, name: 'Maruti Suzuki', nseSymbol: 'MARUTI', icon: () => <BrandLogo src={G_FAVICON('marutisuzuki.com')} alt="Maruti" /> },
  { symbol: 'WIPRO', base: 0, name: 'Wipro Ltd', nseSymbol: 'WIPRO', icon: () => <BrandLogo src={G_FAVICON('wipro.com')} alt="Wipro" /> },
  { symbol: 'HCLTECH', base: 0, name: 'HCL Technologies', nseSymbol: 'HCLTECH', icon: () => <BrandLogo src={G_FAVICON('hcltech.com')} alt="HCL" /> },
  { symbol: 'ICICIBANK', base: 0, name: 'ICICI Bank', nseSymbol: 'ICICIBANK', icon: () => <BrandLogo src={G_FAVICON('icicibank.com')} alt="ICICI" /> },
  { symbol: 'SBIN', base: 0, name: 'State Bank of India', nseSymbol: 'SBIN', icon: () => <BrandLogo src={G_FAVICON('sbi.co.in')} alt="SBI" /> },
  { symbol: 'BAJFINANCE', base: 0, name: 'Bajaj Finance', nseSymbol: 'BAJFINANCE', icon: () => <BrandLogo src={G_FAVICON('bajajfinserv.in')} alt="Bajaj" /> },
  { symbol: 'BHARTIARTL', base: 0, name: 'Bharti Airtel', nseSymbol: 'BHARTIARTL', icon: () => <BrandLogo src={G_FAVICON('airtel.in')} alt="Airtel" /> },
  { symbol: 'KOTAKBANK', base: 0, name: 'Kotak Mahindra Bank', nseSymbol: 'KOTAKBANK', icon: () => <BrandLogo src={G_FAVICON('kotak.com')} alt="Kotak" /> },
  { symbol: 'HINDUNILVR', base: 0, name: 'Hindustan Unilever', nseSymbol: 'HINDUNILVR', icon: () => <BrandLogo src={G_FAVICON('hul.co.in')} alt="HUL" /> },
];

// Fetch live Indian stock data
async function fetchLiveIndianStocks() {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/indian-stocks/live`);
    const result = await response.json();

    if (result.success && result.data) {
      return result.data.map((stock: any) => {
        const baseStock = INDIAN_STOCKS_BASE.find(s => s.symbol === stock.symbol);
        return {
          symbol: stock.symbol,
          icon: baseStock?.icon,
          price: stock.price.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          change: `${stock.changePercent >= 0 ? '+' : ''}${stock.changePercent.toFixed(2)}%`,
        };
      });
    }

    throw new Error('Invalid API response');
  } catch (error) {
    console.error('Failed to fetch live stocks, using fallback:', error);
    return INDIAN_STOCKS_BASE.map(s => {
      const pctChange = (Math.random() - 0.48) * 3;
      const newPrice = s.base * (1 + pctChange / 100);
      return {
        symbol: s.symbol,
        icon: s.icon,
        price: newPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        change: `${pctChange >= 0 ? '+' : ''}${pctChange.toFixed(2)}%`,
      };
    });
  }
}

async function fetchCryptoPrices(): Promise<TickerItem[]> {
  try {
    const params = CRYPTO_SYMBOLS.map(s => `"${s}"`).join(',');
    const { data } = await axios.get(
      `https://api.binance.com/api/v3/ticker/24hr?symbols=[${params}]`
    );
    return data.map((t: any) => {
      const logoSrc = CRYPTO_LOGO[t.symbol];
      return {
        symbol: CRYPTO_DISPLAY[t.symbol] || t.symbol,
        icon: logoSrc ? () => <BrandLogo src={logoSrc} alt={t.symbol} /> : undefined,
        price: parseFloat(t.lastPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        change: `${parseFloat(t.priceChangePercent) >= 0 ? '+' : ''}${parseFloat(t.priceChangePercent).toFixed(2)}%`,
      };
    });
  } catch {
    // Fallback: return empty so ticker still shows Indian stocks
    return [];
  }
}

// ── MarketTicker component with live data ─────────────────────────────────────
const MarketTicker = () => {
  const [items, setItems] = useState<TickerItem[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const [crypto, stocks] = await Promise.all([
      fetchCryptoPrices(),
      fetchLiveIndianStocks(),
    ]);
    setItems([...stocks, ...crypto]);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30_000); // refresh every 30s
    return () => clearInterval(id);
  }, [refresh]);

  if (loading) {
    return (
      <div className="border-b border-[var(--glass-border)] py-1.5 overflow-hidden w-full bg-[var(--color-bg)]">
        <div className="flex animate-ticker whitespace-nowrap">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="inline-flex items-center gap-1.5 px-4 border-r border-[var(--glass-border)] shrink-0">
              <span className="inline-block w-16 h-5 rounded shimmer" />
              <span className="inline-block w-20 h-5 rounded shimmer" />
              <span className="inline-block w-10 h-5 rounded shimmer" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="sticky top-0 left-0 right-0 border-b z-100 border-[var(--glass-border)] py-1.5 overflow-hidden w-full bg-[#fff]">
      <div className="flex animate-ticker whitespace-nowrap">
        {[...items, ...items].map((s, i) => (
          <div key={i} className="inline-flex items-center gap-1.5 px-2 sm:px-4 border-r border-[#ddd] opacity-60 shrink-0">
            <div className="flex items-center gap-1">
              {typeof s.icon === 'function' ? s.icon() : <BarChart3 className="w-3 h-3 text-[#032d18]" />}
              <span className="text-[10px] sm:text-[13px] font-mono font-normal opacity-90 text-[#000]">{s.symbol}</span>
            </div>
            <span className="text-[9px] sm:text-[12px] font-mono text-[var(--color-text)] hidden xs:inline-block">{s.price}</span>
            <span className={`text-[8px] sm:text-[10px] font-mono font-bold ${s.change.startsWith('+') ? 'text-[var(--color-accent)]' : 'text-red-500'}`}>
              {s.change}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const navLinks = [
  { label: 'Home', to: '/', icon: LayoutDashboard },
  { label: 'About Us', to: '/about', icon: Users },
  { label: 'Courses', to: '/services', icon: Package },
  { label: 'Testimonials', to: '/testimonials', icon: MessageSquare },
  { label: 'Contact', to: '/contact', icon: Building2 },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const { isNavbarVisible, showNavbar, hideNavbar, triggerNavbarAutoHide } = useUI();
  const [open, setOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false); // User profile dropdown state
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  const handleLogout = async () => {
    await logout();
    toast('You have been signed out.', 'info');
    navigate('/login');
  };

  const filteredNavLinks = user?.role === 'admin' ? [] : navLinks;

  return (
    <>
      {/* Standalone burger menu for desktop - visible when header is hidden */}
      {!isNavbarVisible && (
        <div className="hidden lg:flex fixed top-10 right-[10px] z-50 p-2 rounded-full bg-[#fff] border border-[#032d18] backdrop-blur-md shadow-lg animate-pulse-continuous" onClick={showNavbar}>
          <button
            className="p-0 rounded-lg text-[var(--color-muted)] hover:text-[var(--color-text)] transition-all"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      )}

      {!open && <MarketTicker />}

      {/* Main navigation header */}
      <nav className={`nav-header group rounded-[5px] fixed top-10 z-50 bg-[#fff] border-b border-[var(--glass-border)] backdrop-blur-md shadow-[0_4px_20px_rgba(0,0,0,0.15),0_2px_10px_rgba(0,0,0,0.1),0_0_0_1px_rgba(0,0,0,0.05)] transition-all duration-1000 ease-in-out transform ${location.pathname.startsWith('/dashboard') ? 'lg:left-[200px] left-0 right-0 w-[70%]' : 'left-0 right-0 w-[90%]'} mx-auto ${isNavbarVisible ? 'translate-y-0 opacity-100' : 'lg:-translate-y-full lg:opacity-0'
        }`}>
        <div className="container-fluid !px-[15px]">
          <div className="relative flex items-center h-14 sm:h-[58px] navbar-logo-link justify-between">
            {/* Logo - hide on desktop when menu is open, show on mobile/tablet */}
            <Link to={user?.role === 'admin' ? '/admin' : '/'} className={`flex items-center justify-between gap-2.5 shrink-0 z-10 transition-all duration-1000 ease-in-out transform stonebench-logo ${isNavbarVisible ? 'lg:translate-x-0 lg:opacity-100' : 'lg:translate-x-full lg:opacity-0 lg:pointer-events-none'
              }`}>
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg overflow-hidden shrink-0 flex items-center justify-center bg-[var(--glass-bg)] border border-[var(--glass-border)]">
                <img src="/stone-bench-logo.jpeg" alt="Stone Bench" className="w-full h-full object-contain"
                  onError={e => { e.currentTarget.style.display = 'none'; }} />
              </div>
              <div className="flex flex-col leading-none">
                <span className="text-[13px] sm:text-sm font-black tracking-tight text-[var(--color-text)]">STONE BENCH</span>
                <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-[var(--color-muted)]">Investments</span>
              </div>
            </Link>

            {/* Center nav - hidden on desktop initial load, visible when desktop menu is open */}
            <div className={`hidden lg:flex absolute right-[40px] w-[70%] justify-end -translate-x-1/2 items-center gap-[25px] transition-all duration-1000 ease-in-out transform ${isNavbarVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'
              }`}>
              {filteredNavLinks.map(l => (
                <Link key={l.to} to={l.to}
                  onClick={triggerNavbarAutoHide}
                  className={`px-2 py-2 rounded-lg text-[13px] font-semibold transition-all whitespace-nowrap ${isActive(l.to)
                    ? 'text-[var(--color-accent)] bg-[rgba(95,156,76,0.08)]'
                    : 'text-[var(--color-muted)] hover:text-[var(--color-text)] hover:bg-[var(--glass-bg)]'
                    }`}>{l.label}</Link>
              ))}
              {user?.role === 'admin' && (
                <Link to="/admin"
                  onClick={triggerNavbarAutoHide}
                  className={`px-2 py-2 rounded-lg text-[13px] font-semibold transition-all whitespace-nowrap ${isActive('/admin') ? 'text-[var(--color-accent)] bg-[rgba(95,156,76,0.08)]' : 'text-[var(--color-muted)] hover:text-[var(--color-accent)]'
                    }`}>Admin</Link>
              )}
              {/* Right actions - hide on desktop when menu is closed */}
              <div className={`flex items-center shrink-0 z-10 transition-all duration-1000 ease-in-out transform ${isNavbarVisible ? 'lg:translate-x-0 lg:opacity-100' : 'lg:translate-x-full lg:opacity-0 lg:pointer-events-none'
                }`}>
                {user ? (
                  <div className="flex items-center gap-3">
                    <div className="text-right hidden sm:block">
                      <p className="text-xs font-bold text-[var(--color-text)] leading-none mb-0.5">{user?.name || 'User'}</p>
                      <p className="text-[12px] text-[var(--color-muted)] font-medium leading-none">{user?.role}</p>
                    </div>
                    <div className="relative">
                      <button
                        onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                        className="w-8 h-8 rounded-full border border-[var(--glass-border)] flex items-center justify-center bg-[var(--color-bg)] text-[var(--color-accent)] font-black text-xs hover:border-[var(--color-accent)] transition-all"
                      >
                        {(user?.name || 'U').charAt(0).toUpperCase()}
                      </button>

                      {/* Dropdown Menu */}
                      {profileDropdownOpen && (
                        <div className="absolute right-0 top-full mt-2 w-48 bg-[var(--color-bg)] rounded-lg shadow-lg border border-[var(--glass-border)] py-1 z-50">
                          <Link
                            to="/dashboard"
                            onClick={() => setProfileDropdownOpen(false)}
                            className="flex items-center px-4 py-2 text-sm text-[var(--color-text)] hover:bg-[var(--glass-bg)] transition-colors"
                          >
                            <LayoutDashboard className="w-4 h-4 mr-2" />
                            My Profile
                          </Link>
                          <button
                            onClick={() => {
                              handleLogout();
                              setProfileDropdownOpen(false);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
                          >
                            <LogOut className="w-4 h-4 mr-2" />
                            Logout
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Link to="/login" className="hidden sm:block px-3 py-1.5 rounded-lg text-[12px] font-semibold text-[var(--color-muted)] hover:text-[var(--color-text)] transition-all">Login</Link>
                    <Link to="/register" className="btn btn-primary btn-sm text-[12px]">Get Started</Link>
                  </div>
                )}
              </div>
            </div>
            {/* Desktop burger menu - visible only on hover */}
            <button className="absolute right-[-13px] hidden lg:flex ml-2 p-2 rounded-lg text-[var(--color-muted)] hover:text-[var(--color-text)] transition-all opacity-0 group-hover:opacity-100 cursor-pointer" onClick={() => isNavbarVisible ? hideNavbar() : showNavbar()}>
              {isNavbarVisible ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            {/* Mobile burger menu - only visible below lg screens */}
            <button className="burger-menu lg:hidden ml-2 p-2 rounded-lg text-[var(--color-muted)] hover:text-[var(--color-text)] transition-all" onClick={() => setOpen(o => !o)}>
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </nav>
      {/* Mobile menu - Premium Design */}
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="menu-list lg:hidden fixed top-0 left-0 right-0 bottom-0 overflow-hidden z-100"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />

          {/* Menu Content */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: "easeInOut", type: "tween" }}
            className="absolute top-0  right-0 bottom-0 w-[85%] sm:w-[75%] max-w-[320px] bg-[var(--color-bg)] shadow-2xl overflow-hidden flex flex-col"
          >
            {/* Menu Header */}
            <div className="relative bg-gradient-to-r from-[var(--color-accent)] to-[#2a7f3e] p-6 text-white">
              <motion.div
                initial={{ scale: 0, rotate: 180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center bg-white/20 backdrop-blur-sm border border-white/30">
                    <img src="/stone-bench-logo.jpeg" alt="Stone Bench" className="w-full h-full object-contain"
                      onError={e => { e.currentTarget.style.display = 'none'; }} />
                  </div>
                  <div className="flex flex-col leading-none">
                    <span className="text-sm font-bold tracking-tight">STONE BENCH</span>
                    <span className="text-[10px] font-medium uppercase tracking-[0.14em] opacity-80">Investments</span>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="p-2 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30 transition-all"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </motion.div>
            </div>

            {/* User Section */}
            {user && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                className="px-6 py-4 border-b border-[var(--glass-border)]"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--color-accent)] to-[#2a7f3e] flex items-center justify-center text-white font-bold text-sm shadow-lg">
                    {(user?.name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[var(--color-text)]">{user?.name || 'User'}</p>
                    <p className="text-[11px] text-[var(--color-muted)] font-medium capitalize">{user?.role}</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Navigation Links */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              <div className="space-y-1">
                {filteredNavLinks.map((l, index) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
                  >
                    <Link
                      to={l.to}
                      onClick={() => setOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${isActive(l.to)
                          ? 'text-white bg-gradient-to-r from-[var(--color-accent)] to-[#2a7f3e] shadow-lg shadow-[var(--color-accent)]/25'
                          : 'text-[var(--color-text)] hover:bg-[var(--glass-bg)] hover:text-[var(--color-accent)]'
                        }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive(l.to)
                          ? 'bg-white/20'
                          : 'bg-[var(--glass-bg)]'
                        }`}>
                        {l.icon && <l.icon className="w-4 h-4" />}
                      </div>
                      <span>{l.label}</span>
                    </Link>
                  </motion.div>
                ))}

                {user?.role === 'admin' && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.8 }}
                  >
                    <Link
                      to="/admin"
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-purple-700 shadow-lg shadow-purple-600/25 transition-all hover:from-purple-700 hover:to-purple-800"
                    >
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/20">
                        <LayoutDashboard className="w-4 h-4" />
                      </div>
                      <span>Admin Panel</span>
                    </Link>
                  </motion.div>
                )}

                {user && user.role !== 'admin' && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.9 }}
                  >
                    <Link
                      to="/dashboard"
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-[var(--color-text)] hover:bg-[var(--glass-bg)] hover:text-[var(--color-accent)] transition-all"
                    >
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[var(--glass-bg)]">
                        <LayoutDashboard className="w-4 h-4" />
                      </div>
                      <span>My Dashboard</span>
                    </Link>
                  </motion.div>
                )}
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="px-4 py-4 border-t border-[var(--glass-border)]">
              {!user ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.5 }}
                  className="flex gap-2"
                >
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-[var(--color-text)] bg-[var(--glass-bg)] hover:bg-[var(--glass-border)] transition-all"
                  >
                    <span>Login</span>
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setOpen(false)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[var(--color-accent)] to-[#2a7f3e] shadow-lg shadow-[var(--color-accent)]/25 hover:from-[#2a7f3e] hover:to-[var(--color-accent)] transition-all"
                  >
                    <span>Register</span>
                  </Link>
                </motion.div>
              ) : (
                <motion.button
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.5 }}
                  onClick={() => { setOpen(false); handleLogout(); }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </motion.button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
}
